import {
  Input,
  OnChanges,
  Renderer2,
  Directive,
  ElementRef,
  HostListener,
  SimpleChanges,
} from '@angular/core';

// ! Example:
// * <img
// *   [src]="avatarUrl"
// *   [appFallbackImage]="DefaultAvatarUrl"  <!-- Optional -->
// *   [containerLevelsUp]="1"
// *   [loadingImage]="'/assets/spinner.svg'"
// * />
// * OR
// * <img
// *   [src]="photoUrl"
// *   appFallbackImage        <!-- no value passed -->
// *   [containerLevelsUp]="2"
// * />

@Directive({
  selector: 'img[appFallbackImage]',
  standalone: false,
})
export class FallbackImageDirective implements OnChanges {
  @Input('appFallbackImage') fallbackUrl?: string;
  @Input() containerLevelsUp: number = 0; //          * levels up to remove on error - (if no fallback!)
  @Input() loadingImage?: string; //                  * loading spinner

  constructor(
    private el: ElementRef<HTMLImageElement>,
    private renderer: Renderer2,
  ) {}

  // * Remove loading background when image loads
  @HostListener('load')
  onLoad() {
    this.removeLoadingBackground();
  }

  // * Handle image load failure: use fallback or remove element
  @HostListener('error')
  onError() {
    const img = this.el.nativeElement;

    if (img.dataset['fallback']) return;

    if (this.fallbackUrl) {
      img.src = this.fallbackUrl;
      img.dataset['fallback'] = 'true';
    } else {
      this.removeElementWithTraversal();
    }
  }

  // * Apply loading background if provided
  ngOnChanges(changes: SimpleChanges) {
    if (changes['loadingImage']) {
      this.applyLoadingBackground();
    }
  }

  private applyLoadingBackground() {
    const style = `url('${this.loadingImage}')`;
    this.renderer.setStyle(this.el.nativeElement, 'background', style);
    this.renderer.setStyle(this.el.nativeElement, 'background-size', 'cover');
    this.renderer.setStyle(
      this.el.nativeElement,
      'background-position',
      'center',
    );
  }

  private removeLoadingBackground() {
    this.renderer.setStyle(this.el.nativeElement, 'background-image', 'none');
  }

  // * Remove this element or a parent (based on containerLevelsUp)
  private removeElementWithTraversal() {
    let target: HTMLElement = this.el.nativeElement;

    for (let i = 0; i < this.containerLevelsUp; i++) {
      if (target.parentNode) {
        target = target.parentNode as HTMLElement;
      } else {
        break;
      }
    }

    if (target.parentNode) {
      this.renderer.removeChild(target.parentNode, target);
    }
  }
}
